/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import styles from "@pacific/App/Components/ProductBarcodeScanner/scanner.styles";
import ScannerModalContent from "@pacific/App/Components/ProductBarcodeScanner/ScannerModalContent";
import Barcode from "@pacific/XDL/Icons/Barcode";
import React, { useState } from "react";

interface ScanResult {
    text: string;
    isUrl?: boolean;
}

const ProductBarcodeScanner = ({ history }: HasHistory) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [result, setResult] = useState<ScanResult>({ text: "", isUrl: false });

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };
    const handleResultChange = (text: string, isUrl?: boolean) => {
        setResult({ text, isUrl });
    };
    const handleError = (errorMessage: any) => {
        logger.error(errorMessage);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setResult({ text: "", isUrl: false });
    };

    const doSearch = () => {
        if (!result || !result.text) {
            return;
        }
        if (result.isUrl) {
            if (typeof window !== "undefined") {
                window.location.href = result.text;
            }
            return;
        }
        history.push(`/Search?query=${result.text}`);
        handleCloseModal();
    };

    return (
        <>
            <Clickable {...styles.barcodeButton} onClick={handleOpenModal} aria-label="Search by Barcode">
                <Icon {...styles.barcodeIcon} src={Barcode} />
                <VisuallyHidden>{translate("Search by Barcode Or QR code")}</VisuallyHidden>
            </Clickable>
            <Modal
                {...styles.mobileBarcodeModal}
                isOpen={isModalOpen}
                closeOnEsc={true}
                handleClose={handleCloseModal}
                headline={translate("Search by Barcode Or QR Code")}
                sizeVariant="medium"
            >
                <ScannerModalContent onChange={handleResultChange} onError={handleError} isOpen={isModalOpen} />
                {result.isUrl ? <Link href={result.text}>{result.text}</Link> : <Typography>{result.text}</Typography>}
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button onClick={handleCloseModal} variant="tertiary" sizeVariant="medium" aria-label="Close">
                        {translate("Close")}
                    </Button>
                    <Button
                        onClick={doSearch}
                        variant="primary"
                        sizeVariant="medium"
                        disabled={!result.text}
                        aria-label="Search"
                    >
                        {translate("Search")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default withHistory(ProductBarcodeScanner);
